import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";

const HeroScene = lazy(() => import("./HeroScene"));

export default function Hero() {
    const { t } = useI18n();
    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden flex items-center"
        >
            {/* 3D Background */}
            <div className="absolute inset-0 z-0">
                <Suspense fallback={null}>
                    <HeroScene />
                </Suspense>
            </div>

            {/* Gradient overlays */}
            <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(60%_60%_at_50%_40%,rgba(0,85,255,0.2)_0%,rgba(0,229,255,0.04)_40%,rgba(0,0,0,0)_70%)]" />
            <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-transparent via-transparent to-ink-950" />
            <div className="pointer-events-none absolute inset-0 z-10 grid-bg opacity-25" />

            {/* Content */}
            <div className="container-page relative z-20 pt-24 pb-16 md:pt-28 md:pb-24">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="max-w-3xl"
                >
                    <span data-testid="hero-eyebrow" className="eyebrow">
                        <Sparkles className="h-3.5 w-3.5 text-brand-glow" />
                        {t("hero.eyebrow")}
                    </span>

                    <h1
                        data-testid="hero-title"
                        className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]"
                    >
                        <span className="block">{t("hero.title1")}</span>
                        <span className="block gradient-text pb-2">{t("hero.title2")}</span>
                        <span className="block text-white/85 text-3xl sm:text-4xl lg:text-5xl mt-2 font-medium leading-[1.15]">
                            {t("hero.title3")}
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="mt-6 max-w-xl text-base md:text-lg text-white/65 leading-relaxed"
                    >
                        {t("hero.subtitle")}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="mt-9 flex flex-wrap items-center gap-3"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-primary"
                            className="btn-primary group"
                        >
                            {t("hero.cta1")}
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </a>
                        <a
                            href="#services"
                            data-testid="hero-cta-secondary"
                            className="btn-ghost"
                        >
                            {t("hero.cta2")}
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/55"
                    >
                        <span className="inline-flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-brand-glow" />
                            {t("hero.badge1")}
                        </span>
                        <span className="hidden md:inline-block h-1 w-1 rounded-full bg-white/20" />
                        <span>{t("hero.badge2")}</span>
                    </motion.div>
                </motion.div>
            </div>

            {/* Scroll hint */}
            <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                <span>Scroll</span>
                <span className="block h-8 w-px bg-gradient-to-b from-white/40 to-transparent" />
            </div>
        </section>
    );
}
